import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import type { ReferenceDataColumn } from "@/types/reference-data";

export interface RecordsSearch {
  column?: string;
  term: string;
}

/** Permite aplicar a digitação pendente antes de abrir a edição em lote. */
export interface RecordsSearchBarHandle {
  commitSearch: () => void;
}

export const RecordsSearchBar = forwardRef<RecordsSearchBarHandle, {
  columns: ReferenceDataColumn[];
  disabled?: boolean;
  onSearch: (search: RecordsSearch) => void;
}>(function RecordsSearchBar({ columns, disabled = false, onSearch }, ref) {
  const { t } = useTranslation();
  const id = useId();
  const [term, setTerm] = useState("");
  const [column, setColumn] = useState("");
  // A string é estável entre renders: o hook não renova o timer a cada leitura da tabela.
  const [debouncedTerm, commitNow] = useDebouncedSearch(term);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  const emitted = useRef<RecordsSearch>({ term: "" });
  const apply = useCallback((search: RecordsSearch) => {
    if (emitted.current.term === search.term && emitted.current.column === search.column) return;
    emitted.current = search;
    onSearchRef.current(search);
  }, []);

  useEffect(() => {
    // Limpar é imediato; um valor antigo do debounce nunca pode reabrir a busca apagada.
    if (!disabled && debouncedTerm === term) {
      apply({ column: column || undefined, term: debouncedTerm.trim() });
    }
  }, [debouncedTerm, term, column, disabled, apply]);

  const submit = () => {
    if (disabled) return;
    commitNow();
    apply({ column: column || undefined, term: term.trim() });
  };
  useImperativeHandle(ref, () => ({ commitSearch: submit }));
  const clear = () => {
    setTerm("");
    setColumn("");
    apply({ term: "" });
  };

  const searchable = columns.filter(
    (c) => c.data_type !== "json" && c.data_type !== "jsonb" && c.name !== "version",
  );
  return (
    <form
      role="search"
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <label htmlFor={`${id}-column`} className="text-[11px] text-[#596b85]">{t("Column")}</label>
        <select
          id={`${id}-column`}
          disabled={disabled}
          value={column}
          onChange={(event) => {
            const next = event.target.value;
            setColumn(next);
            if (emitted.current.term) apply({ column: next || undefined, term: emitted.current.term });
          }}
          className="h-8 max-w-full rounded-md border border-[#e8ebf0] bg-white px-2 text-[12px] text-[#08254c] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="">{t("Code and name")}</option>
          {searchable.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          <option value="version">version</option>
        </select>
      </div>
      <div className="flex min-w-[140px] flex-1 flex-col gap-1">
        <label htmlFor={`${id}-term`} className="text-[11px] text-[#596b85]">{t("Search")}</label>
        <input
          id={`${id}-term`}
          type="search"
          placeholder={t("Enter a value…")}
          maxLength={200}
          disabled={disabled}
          value={term}
          onChange={(event) => {
            const next = event.target.value;
            if (next === "") clear();
            else setTerm(next);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              submit();
            }
          }}
          className="h-8 min-w-0 rounded-md border border-[#e8ebf0] bg-white px-2.5 text-[12px] text-[#08254c] placeholder:text-[#7b8799] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c] disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>
      <Button type="submit" variant="outline" size="sm" aria-label={t("Search")} disabled={disabled}>
        <Search aria-hidden="true" className="h-3.5 w-3.5" />
      </Button>
      <Button type="button" variant="outline" size="sm" aria-label={t("Clear search")} disabled={disabled} onClick={clear}>
        <X aria-hidden="true" className="h-3.5 w-3.5" />
      </Button>
    </form>
  );
});
